import { addDimensions } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import {
    getSplitNames,
    getTreatments,
    initSplitSdk,
} from '@splitsoftware/splitio-redux';
import { Dispatch } from 'redux';
import { TURN_ON_SET_PAYMENT_METHOD_BFF } from '../constants/splitio';
import { ALL_PAYMENT_METHOD_TYPES } from '../models/paymentMethods/enums';
import {
    getActiveFFsFromCore,
    getIsAppRebrandActive,
    isAndroidApp as getIsAndroidApp,
    isEventbriteTLD as getIsEventbriteTLD,
    isIosApp as getIsIosApp,
} from '../selectors/app';
import {
    getEventCountryCode,
    getEventOrganizationId,
    getHasPromoCode,
    getIsFreeEvent,
    getIsReservedSeating,
} from '../selectors/event';
import { isSplitTreatmentEnabled } from '../selectors/splitio';
import { getHasTicketsWithPromoCodeApplied } from '../selectors/tickets';
import { CheckoutState } from '../state';
import { getNativeAppVersion } from '../utils/browser';
import {
    PAYMENT_METHOD_CLEAR_OPERATION,
    setPaymentMethodBffTreatments,
} from '../utils/resiliencyFeatureFlag';
import {
    findArrayAttributesTreatment,
    getSplitioClient,
} from '../utils/splitio';
import { saveSplitIOMultiValueTargettingRulesAction } from './splitioMultiValueTargettingRules';

export const initializeSplitioSdk =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const {
            app: {
                isProd,
                splitIOProdKey,
                event: { id: eventId },
            },
        } = getState();

        const countryCode = getEventCountryCode(getState());
        const hasPromoCode = getHasPromoCode(getState());
        const hasTicketsWithPromoCodeApplied =
            getHasTicketsWithPromoCodeApplied(getState());
        const isAppRebrandActive = getIsAppRebrandActive(getState());
        const isAndroidApp = getIsAndroidApp(getState());
        const isEventbriteTLD = getIsEventbriteTLD(getState());
        const isIosApp = getIsIosApp(getState());
        const isReservedSeating = getIsReservedSeating(getState());
        const nativeAppVersion = getNativeAppVersion();
        const organizationId = getEventOrganizationId(getState());
        const isFree = Boolean(getIsFreeEvent(getState()));

        const extraAttributes = {
            countryCode,
            eventId,
            hasPromoCode,
            hasTicketsWithPromoCodeApplied,
            isAndroidApp,
            isAppRebrandActive,
            isEventbriteTLD,
            isIosApp,
            isReservedSeating,
            nativeAppVersion,
            organizationId,
            isFree,
        };

        // Don't initialize SDK if there is no prod key (eg. unit tests)
        if (!splitIOProdKey) return;

        await dispatch(
            initSplitSdk({
                config: getSplitioClient(
                    eventId,
                    isProd ? splitIOProdKey : null,
                ),
            }) as $FixMe,
        );

        const splitNames = getSplitNames();

        // Apply attributes to evaluate splits
        dispatch(
            getTreatments({
                splitNames: splitNames,
                attributes: extraAttributes,
            }) as any,
        );

        // Send split values to analytics
        const state = getState();
        const activeFFsFromCore = getActiveFFsFromCore(state);
        const activeSplits = splitNames.flatMap((name) => {
            const value = isSplitTreatmentEnabled(state, name);
            return value ? [name] : [];
        });

        // Turn on/off the payment method BFF by payment method, plus 'clear' for the delete
        const turnOnSetPaymentMethodBffTreatments =
            await findArrayAttributesTreatment({
                featureFlag: TURN_ON_SET_PAYMENT_METHOD_BFF,
                attributes: [
                    ...ALL_PAYMENT_METHOD_TYPES,
                    PAYMENT_METHOD_CLEAR_OPERATION,
                ],
                attributeName: 'paymentMethod',
                getTreatments,
                extraAttributes: extraAttributes,
            });

        setPaymentMethodBffTreatments(turnOnSetPaymentMethodBffTreatments);

        dispatch(
            saveSplitIOMultiValueTargettingRulesAction({
                [TURN_ON_SET_PAYMENT_METHOD_BFF]:
                    turnOnSetPaymentMethodBffTreatments,
            }),
        );

        addDimensions({
            experimentId: [...activeSplits, ...activeFFsFromCore]
                .sort()
                .join(','),
        });

        return Promise.resolve();
    };
